import type { SupabaseClient } from '@supabase/supabase-js';
import type { EdprDocument, BdoScrapedEdprDocument, EdprRequestRow } from '../../types/edpr';
import type { InvalidateFn } from '../base';
import { CompanyValidator } from '../../utils/CompanyValidator';

// ── Diff types ─────────────────────────────────────────────────────────────

export type EdprSyncAction = 'update' | 'unchanged' | 'orphan' | 'ambiguous';

/** Fields we write from the scrape — keyed by DB column name on edpr_documents. */
export type EdprSyncPatch = Partial<Pick<EdprDocument,
  | 'edpr_number'
  | 'edpr_issue_date'
  | 'edpr_bdo_link'
  | 'bdo_guid'
  | 'bdo_status'
  | 'bdo_issuer'
  | 'bdo_synced_at'
  | 'bdo_synced_by'
>>;

export interface EdprSyncDiff {
  action: EdprSyncAction;
  /** The scraped row (kept for display) */
  scraped: BdoScrapedEdprDocument;
  /** Matched existing DB document (null when `action === 'orphan'`) */
  existing: EdprDocument | null;
  /** All candidates considered when disambiguating (populated for 'ambiguous') */
  candidates?: EdprDocument[];
  /** Fields that would change if the diff is applied */
  changedFields: (keyof EdprSyncPatch)[];
  /** The patch to apply (populated for 'update' and 'ambiguous') */
  patch?: EdprSyncPatch;
  /** Why this match was chosen — shown in the review modal */
  matchReason?: string;
}

/** Candidate request enriched with the app-side documents issued against it. */
type RequestWithDocs = EdprRequestRow & { edpr_documents: Array<{ id: number }> };

// ── Helpers ────────────────────────────────────────────────────────────────

/** "2026-04-16T14:12:00" → "2026-04-16" */
function toDateOnly(iso: string | null): string | null {
  if (!iso) return null;
  const match = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/** Extract guid from existing edpr_bdo_link (if any). */
function guidFromLink(link: string | null | undefined): string | null {
  if (!link) return null;
  const m = link.match(/\/Preview\/([a-f0-9-]{36})/i);
  return m ? m[1].toLowerCase() : null;
}

/** "2026/EDPR/000002455/83/000690506/1" → "2026/EDPR/000002455/83".
 *  BDO builds the document number by appending the issuer registry + sequence to the
 *  request number; the segment-count guard keeps format drift from producing garbage. */
function requestNumberFromEdprNumber(edprNumber: string | null): string | null {
  if (!edprNumber) return null;
  const parts = edprNumber.split('/');
  if (parts.length !== 6 || parts[1] !== 'EDPR') return null;
  return parts.slice(0, 4).join('/');
}

/** A document with any BDO identity already on it belongs to some BDO document; only the
 *  exact-identity strategies (edpr_number / guid) may match it, never the heuristic ones. */
function isUnbound(d: EdprDocument): boolean {
  return d.edpr_number == null && d.bdo_guid == null && guidFromLink(d.edpr_bdo_link) == null;
}

/** Requests in these BDO states are dead — their own BDO document will never arrive, so
 *  their stranded app documents may be donated to a re-submission sibling. */
const DEAD_REQUEST_STATUSES = new Set(['Wycofany', 'Odrzucony']);

interface MatchResult {
  existing: EdprDocument | null;
  candidates?: EdprDocument[];
  reason: string;
  confident: boolean;
}

/** Tolerance used when comparing scraped vs stored requested_mass (tonnes). */
const MASS_TOLERANCE_T = 0.001;

function massMatches(a: number | null, b: number | null): boolean {
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= MASS_TOLERANCE_T;
}

function nipEq(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = CompanyValidator.normalizeNip(a ?? null);
  const nb = CompanyValidator.normalizeNip(b ?? null);
  return !!na && !!nb && na === nb;
}

// ── Service ────────────────────────────────────────────────────────────────

export class EdprDocumentSyncService {
  constructor(
    private readonly sb: SupabaseClient,
    private readonly invalidate?: InvalidateFn,
  ) {}

  /** Build the patch we'd apply to an edpr_documents row from a scraped row. */
  scrapedToPatch(scraped: BdoScrapedEdprDocument, userId?: string): EdprSyncPatch {
    return {
      edpr_number: scraped.edpr_number ?? undefined,
      edpr_issue_date: toDateOnly(scraped.issue_date) ?? undefined,
      edpr_bdo_link: scraped.preview_url ?? undefined,
      bdo_guid: scraped.bdo_guid ?? undefined,
      bdo_status: scraped.status ?? undefined,
      bdo_issuer: scraped.issuer ?? undefined,
      bdo_synced_at: new Date().toISOString(),
      bdo_synced_by: userId ?? undefined,
    };
  }

  /** Load candidate docs for matching. Returns only non-deleted rows. */
  async loadCandidateDocuments(): Promise<EdprDocument[]> {
    const { data, error } = await this.sb
      .from('edpr_documents')
      .select(`
        id, internal_number, year, quarter, household_waste,
        organizacja_odzysku_company_id, edpr_number, edpr_issue_date, edpr_bdo_link,
        request_number, request_date, request_bdo_link,
        drive_folder_id, invoices_pdf_drive_id, ie599_pdf_drive_id, annex7_pdf_drive_id,
        bdo_copy_drive_id, wniosek_pdf_drive_id,
        signed_invoices_pdf_drive_id, signed_ie599_pdf_drive_id, signed_annex7_pdf_drive_id,
        docs_prepared_at, edpr_request_id, notes,
        bdo_guid, bdo_status, bdo_issuer, bdo_synced_at, bdo_synced_by,
        is_deleted, created_at, updated_at, created_by,
        organizacja_odzysku_company:companies!edpr_documents_organizacja_odzysku_company_id_fkey(id, short_name, full_name)
      `)
      .eq('is_deleted', false);
    if (error) throw error;
    return (data ?? []) as unknown as EdprDocument[];
  }

  /** Load requests with their non-deleted documents attached — used for NIP/mass matching. */
  async loadCandidateRequests(): Promise<RequestWithDocs[]> {
    const { data, error } = await this.sb
      .from('edpr_requests')
      .select(`
        id, request_number, bdo_guid, year, status, request_date, submitter,
        applicant_company, applicant_company_bdo, applicant_company_nip, applicant_company_id,
        applicant_phone, applicant_email,
        recipient_company, recipient_company_bdo, recipient_company_nip,
        intended_company, intended_company_bdo, intended_company_nip, intended_company_id,
        waste_code, waste_code_description, household_waste, art24,
        requested_mass, additional_info,
        is_deleted, created_at, updated_at, synced_at, synced_by,
        edpr_documents:edpr_documents!edpr_request_id(id, is_deleted)
      `)
      .eq('is_deleted', false);
    if (error) throw error;
    type RawRow = Omit<RequestWithDocs, 'edpr_documents'> & {
      edpr_documents: Array<{ id: number; is_deleted: boolean }> | null;
    };
    const rows = (data ?? []) as unknown as RawRow[];
    // Drop soft-deleted child documents so they don't count toward match resolution
    return rows.map(r => ({
      ...r,
      edpr_documents: (r.edpr_documents ?? []).filter(d => !d.is_deleted).map(d => ({ id: d.id })),
    }));
  }

  /** Resolve a matched request to an edpr_document (or set of ambiguous candidates). */
  private resolveRequestToDoc(
    request: RequestWithDocs,
    docs: EdprDocument[],
    eligible: (doc: EdprDocument) => boolean,
    reason: string,
  ): MatchResult {
    const live = request.edpr_documents
      .map(d => docs.find(doc => doc.id === d.id))
      .filter((d): d is EdprDocument => !!d && eligible(d));
    if (live.length === 1) {
      return { existing: live[0], reason: `${reason} → wniosek ${request.request_number}`, confident: true };
    }
    if (live.length > 1) {
      return { existing: null, candidates: live, reason: `${reason} → wniosek ${request.request_number} ma ${live.length} dokumentów`, confident: false };
    }
    return { existing: null, reason: `${reason} → wniosek ${request.request_number} nie ma dokumentu w aplikacji`, confident: false };
  }

  /**
   * Match a scraped row against existing edpr_documents.
   * Priority:
   *   1. Exact edpr_number → edpr_document  (confident)
   *   2. Exact bdo_guid → edpr_document     (confident)
   *   3. request_number (derived from the BDO document number, else from the scrape)
   *      → edpr_requests → edpr_document
   *   4. Triple NIP (applicant + intended + recipient) + waste_code + requested_mass
   *      → edpr_requests → edpr_document  (heuristic — only in the second matching pass)
   */
  private findMatch(
    scraped: BdoScrapedEdprDocument,
    docs: EdprDocument[],
    requests: RequestWithDocs[],
    claimed: Set<number>,
    allowHeuristics: boolean,
  ): MatchResult {
    const unclaimed = (d: EdprDocument) => !claimed.has(d.id);
    // Steps 3–4 bind by inference, so they may only take documents nothing else owns —
    // neither claimed by another scraped row nor already carrying another BDO identity.
    const bindable = (d: EdprDocument) => unclaimed(d) && isUnbound(d);

    // 1. Exact edpr_number match
    if (scraped.edpr_number) {
      const match = docs.find(c => c.edpr_number === scraped.edpr_number && unclaimed(c));
      if (match) return { existing: match, reason: `edpr_number=${scraped.edpr_number}`, confident: true };
    }

    // 2. GUID match — either stored bdo_guid column, or extracted from edpr_bdo_link
    if (scraped.bdo_guid) {
      const match = docs.find(c =>
        unclaimed(c) && (
          c.bdo_guid === scraped.bdo_guid ||
          guidFromLink(c.edpr_bdo_link) === scraped.bdo_guid
        )
      );
      if (match) return { existing: match, reason: `bdo_guid=${scraped.bdo_guid}`, confident: true };
    }

    // 3. Request number → edpr_requests → edpr_document(s). The BDO document number embeds
    //    the request number it was issued against (first 4 of its 6 segments), so derive it
    //    from there; the detail-page scrape rarely exposes it directly.
    let requestMissReason: string | null = null;
    const requestNumber = requestNumberFromEdprNumber(scraped.edpr_number) ?? scraped.request_number;
    if (requestNumber) {
      const req = requests.find(r => r.request_number === requestNumber);
      if (req) {
        const resolved = this.resolveRequestToDoc(req, docs, bindable, `request_number=${requestNumber}`);
        if (resolved.existing || (resolved.candidates?.length ?? 0) > 1) return resolved;
        // Request identified but doc-less — remember the precise reason and let the NIP
        // fallback try; the document may hang off a re-submission sibling request.
        requestMissReason = resolved.reason;
      }
    }

    const noMatch = (): MatchResult => ({
      existing: null,
      reason: requestMissReason ?? 'nie znaleziono pasującego dokumentu (edpr_number/guid/wniosek/NIPs)',
      confident: false,
    });
    if (!allowHeuristics) return noMatch();

    // 4. Triple NIP + waste_code + requested_mass → edpr_requests
    const nipMatches = requests.filter(r =>
      nipEq(r.applicant_company_nip, scraped.applicant_company_nip) &&
      nipEq(r.intended_company_nip, scraped.intended_company_nip) &&
      nipEq(r.recipient_company_nip, scraped.recipient_company_nip)
    );
    if (nipMatches.length > 0) {
      // Waste code — require exact match when both sides have it; skip the check otherwise.
      const scrapedWaste = scraped.waste_code?.trim() ?? null;
      const wasteFiltered = scrapedWaste
        ? nipMatches.filter(r => {
            const rw = r.waste_code?.trim() ?? null;
            return rw ? rw === scrapedWaste || scrapedWaste.startsWith(rw) : true;
          })
        : nipMatches;
      // Mass — narrow to same requested_mass within tolerance when we have it on both sides.
      const massFiltered = scraped.requested_mass != null
        ? wasteFiltered.filter(r => massMatches(r.requested_mass, scraped.requested_mass))
        : wasteFiltered;
      const finalists = massFiltered.length > 0 ? massFiltered : wasteFiltered;

      if (finalists.length === 1) {
        const resolved = this.resolveRequestToDoc(finalists[0], docs, bindable, `NIPs + odpad + masa`);
        if (resolved.existing || (resolved.candidates?.length ?? 0) > 1) return resolved;
        return { existing: null, reason: requestMissReason ?? resolved.reason, confident: false };
      }
      if (finalists.length > 1) {
        // Aggregate docs across all finalist requests — a lone document donated by a dead
        // (withdrawn/rejected) re-submission sibling is the match; a live sibling keeps its
        // document reserved for its own BDO document; several candidates are ambiguous
        const candidates: EdprDocument[] = [];
        for (const r of finalists) {
          for (const ref of r.edpr_documents) {
            const d = docs.find(doc => doc.id === ref.id);
            if (d && bindable(d) && !candidates.includes(d)) candidates.push(d);
          }
        }
        if (candidates.length === 1) {
          const donor = finalists.find(r => r.edpr_documents.some(ref => ref.id === candidates[0].id));
          if (donor && DEAD_REQUEST_STATUSES.has(donor.status ?? '')) {
            return { existing: candidates[0], reason: `NIPs + odpad + masa → jedyny dokument (wniosek ${donor.request_number}, ${donor.status})`, confident: false };
          }
          return { existing: null, reason: requestMissReason ?? `${finalists.length} wniosków pasuje, ale jedyny dokument należy do innego aktywnego wniosku`, confident: false };
        }
        if (candidates.length > 1) {
          return { existing: null, candidates, reason: `${finalists.length} wniosków pasuje (NIPs + odpad + masa) — wybierz ręcznie`, confident: false };
        }
        return { existing: null, reason: requestMissReason ?? `${finalists.length} wniosków pasuje, ale brak dokumentów w aplikacji`, confident: false };
      }
    }

    return noMatch();
  }

  /**
   * Compute diffs between scraped BDO EDPRs and existing edpr_documents.
   * Returns one diff per scraped row.
   */
  async computeDiff(
    scraped: BdoScrapedEdprDocument[],
    userId?: string,
  ): Promise<EdprSyncDiff[]> {
    const [docs, requests] = await Promise.all([
      this.loadCandidateDocuments(),
      this.loadCandidateRequests(),
    ]);
    const now = new Date().toISOString();
    const diffs: EdprSyncDiff[] = [];

    // Track which docs have already been claimed to prevent double-matching
    const claimed = new Set<number>();

    // Two passes: identity + request-number matches claim their documents first, so the
    // NIP heuristics can never take a document another scraped row owns deterministically.
    const deterministic: Array<MatchResult | null> = scraped.map(s => {
      const m = this.findMatch(s, docs, requests, claimed, false);
      if (!m.existing) return null;
      claimed.add(m.existing.id);
      return m;
    });
    const matches: MatchResult[] = deterministic.map((m, i) => {
      if (m) return m;
      const h = this.findMatch(scraped[i], docs, requests, claimed, true);
      if (h.existing) claimed.add(h.existing.id);
      return h;
    });

    for (const [i, s] of scraped.entries()) {
      const patch = this.scrapedToPatch(s, userId);
      const match = matches[i];

      if (match.existing) {
        const changedFields: (keyof EdprSyncPatch)[] = [];
        const existing = match.existing;

        // Compare each patch field to existing row
        if (patch.edpr_number && patch.edpr_number !== existing.edpr_number) changedFields.push('edpr_number');
        if (patch.edpr_issue_date && patch.edpr_issue_date !== existing.edpr_issue_date) changedFields.push('edpr_issue_date');
        if (patch.edpr_bdo_link && patch.edpr_bdo_link !== existing.edpr_bdo_link) changedFields.push('edpr_bdo_link');
        if (patch.bdo_guid && patch.bdo_guid !== existing.bdo_guid) changedFields.push('bdo_guid');
        if (patch.bdo_status && patch.bdo_status !== existing.bdo_status) changedFields.push('bdo_status');
        if (patch.bdo_issuer && patch.bdo_issuer !== existing.bdo_issuer) changedFields.push('bdo_issuer');

        // Always refresh timestamp + user (even if nothing else changed)
        const realChanges = changedFields.length > 0;
        if (realChanges) {
          changedFields.push('bdo_synced_at');
          if (userId) changedFields.push('bdo_synced_by');
        }

        diffs.push({
          action: realChanges ? 'update' : 'unchanged',
          scraped: s,
          existing,
          changedFields,
          patch: realChanges ? { ...patch, bdo_synced_at: now, bdo_synced_by: userId ?? null } : undefined,
          matchReason: match.reason,
        });
      } else if (match.candidates && match.candidates.length > 1) {
        diffs.push({
          action: 'ambiguous',
          scraped: s,
          existing: null,
          candidates: match.candidates,
          changedFields: [],
          patch,
          matchReason: match.reason,
        });
      } else {
        diffs.push({
          action: 'orphan',
          scraped: s,
          existing: null,
          changedFields: [],
          matchReason: match.reason,
        });
      }
    }

    return diffs;
  }

  /**
   * Apply selected diffs. For 'ambiguous' entries, caller should resolve them
   * by assigning `existing` before calling apply.
   */
  async applyDiffs(
    diffs: Array<EdprSyncDiff & { existing: EdprDocument }>,
  ): Promise<number> {
    if (diffs.length === 0) return 0;

    // Update each matched row individually (no uniform upsert key — we update by id)
    let updated = 0;
    for (const diff of diffs) {
      if (!diff.patch) continue;
      const { error } = await this.sb
        .from('edpr_documents')
        .update(diff.patch)
        .eq('id', diff.existing.id);
      if (error) throw error;
      updated++;
    }

    this.invalidate?.({ resource: 'edpr_documents', invalidates: ['list', 'detail', 'resourceAll'] });
    return updated;
  }
}
